import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPoints } from '@kidshome/i18n';
import {
  type CosmeticCatalogItemResponse,
  type CosmeticCatalogResponse,
} from '@kidshome/api-contract';
import { ActionSheet, EmptyState } from '@/shared/ui';
import { tDynamic } from '@/shared/lib/i18n';
import { groupByTheme } from '@/widgets/collectible';
import { CurrencyGlyph, useScopedCurrencyName, useScopedCurrencyNameFor } from '@/entities/hero';
import { useHouseholdContext } from '@/shared/lib/household';
import { usePurchaseCosmetic } from '@/features/purchase-cosmetic';
import { useEquipCosmetic } from '@/features/equip-cosmetic';
import {
  WardrobeTabBar,
  wardrobePanelId,
  wardrobeTabId,
} from '@/widgets/cosmetics-shop/WardrobeTabBar';
import { CosmeticTile } from '@/widgets/cosmetics-shop/CosmeticTile';
import { CosmeticPreview } from '@/widgets/cosmetics-shop/CosmeticPreview';
import { CheckGlyph } from '@/widgets/cosmetics-shop/wardrobe-glyphs';

/** A stable empty pending-set — the preview (platform) path never has an in-flight mutation. */
const EMPTY_PENDING: ReadonlySet<string> = new Set();

/**
 * Within a place, group by what the kid can act on: owned first, then ready-to-unlock, then still-saving.
 * We deliberately do NOT rank the *equipped* item ahead of other owned looks — equipping is a one-tap toggle,
 * and promoting the just-equipped tile to the front re-sorts the grid under the kid's finger (a disorienting
 * jump). Equipped is an owned look, so it stays put in the owned group; the selection ring/tick shows it's
 * worn without moving it. Ranks are stable across equip toggles → no layout shift on wear.
 */
function rank(i: CosmeticCatalogItemResponse): number {
  if (i.owned) return 0;
  return i.affordable ? 1 : 2;
}

/**
 * The wardrobe LEGEND — one always-visible row that teaches the two corner symbols a kid (and a parent) meets
 * on the tiles, using the EXACT same glyphs: an effort-star ✨ = "save ✨ to unlock", a tick = "wearing now".
 * No modal and no hover (a 4-8yo non-reader can't trigger those) — the key is simply always present, so each
 * tile corner is captioned once. There is no coin and no padlock any more (ADR-0101): every look is reachable.
 */
function WardrobeLegend(): React.ReactElement {
  const { t } = useTranslation('family');
  const currency = useScopedCurrencyName();
  return (
    <div className="wardrobe-legend" role="list">
      <span className="legend-chip" role="listitem">
        <span className="legend-glyph star" aria-hidden="true">
          <CurrencyGlyph />
        </span>
        {t('shop.legend.stars', { currency })}
      </span>
      <span className="legend-chip" role="listitem">
        <span className="legend-glyph on" aria-hidden="true">
          <CheckGlyph />
        </span>
        {t('shop.legend.wearing')}
      </span>
    </div>
  );
}

/**
 * A compact ✨ PROGRESS RING (ADR-0101) — the kid sees how close their banked ✨ is to a look, never a raw
 * decimal. An SVG donut whose arc fills to `pct`; the centre carries the "≈ N days" label. Decorative
 * (aria-hidden) — the surrounding sheet text carries the accessible meaning.
 */
function StarRing({
  pct,
  label,
  ready,
}: {
  pct: number;
  label: string;
  ready: boolean;
}): React.ReactElement {
  const r = 26;
  const circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circ;
  return (
    <span className={`star-ring${ready ? ' is-ready' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 64 64" width={64} height={64}>
        <circle className="ring-track" cx="32" cy="32" r={r} fill="none" />
        <circle
          className="ring-fill"
          cx="32"
          cy="32"
          r={r}
          fill="none"
          strokeDasharray={`${dash} ${circ}`}
          transform="rotate(-90 32 32)"
        />
      </svg>
      <span className="ring-center">
        <CurrencyGlyph size={14} />
        <span className="ring-days">{label}</span>
      </span>
    </span>
  );
}

/**
 * The cosmetics WARDROBE body (FSD `widgets`, ADR-0098 redesign · ADR-0101 effort-stars). The sticky hero
 * stage lives on the page above; here a non-scrolling {@link WardrobeTabBar} swaps the grid to show ONLY the
 * active wear-region as art-first {@link CosmeticTile}s, so the kid reaches any item in one tap. Equip is one
 * tap on the tile; unlocking opens a kind try-on-then-confirm sheet (never one-tap spend — ADR-0029). The
 * kid spends THEIR OWN effort-stars (✨); there is no coin price and no level gate (ADR-0101).
 */
export function CosmeticsShop({
  catalog,
  childId,
  idScope = '',
  preview,
}: {
  catalog: CosmeticCatalogResponse;
  /** The signed-in child's id (for balance-cache invalidation on purchase); null on a non-self view. */
  childId: string | null;
  /** Namespaces the tab/panel ids so several wardrobes can share one page (the platform renders one per
   *  hero). Omit on the kid surface (a single wardrobe). */
  idScope?: string;
  /**
   * PURE-PREVIEW mode (platform-admin hero inspector, ADR-0097 §7). A platform admin has no child/household
   * to spend for, so the live equip/purchase mutations are bypassed: every tap is a LOCAL try-on
   * (`onTryOn`) with no ownership / hero-level / ✨-balance gate, and the buy sheet never opens. When set,
   * the parent owns the equipped look and feeds it back through `catalog` (the same component, no restrictions).
   */
  preview?: { onTryOn: (item: CosmeticCatalogItemResponse) => void };
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { t: tPlay } = useTranslation('play');
  // The live mutations are always instantiated (hook rules) but BYPASSED in preview — there is no kid here.
  const livePurchase = usePurchaseCosmetic({ childId });
  const liveEquip = useEquipCosmetic();
  const buying = preview ? EMPTY_PENDING : livePurchase.pendingItemIds;
  const equipping = preview ? EMPTY_PENDING : liveEquip.pendingItemIds;
  // Any tap (equip an owned look, or "buy" an unowned one) routes to the local try-on in preview; live it
  // equips / opens the confirm-sheet. Owned/worn tiles call onEquipToggle, priced tiles call onBuyIntent —
  // both fold into onTryOn so the admin can put on ANYTHING without owning or affording it.
  const onEquipToggle = preview
    ? (itemId: string): void => {
        const item = catalog.items.find((i) => i.itemId === itemId);
        if (item) preview.onTryOn(item);
      }
    : liveEquip.toggle;

  const grouped = useMemo(() => {
    const byPlace = new Map<string, CosmeticCatalogItemResponse[]>();
    for (const item of catalog.items) {
      const list = byPlace.get(item.place) ?? [];
      list.push(item);
      byPlace.set(item.place, list);
    }
    // The chosen hero DECLARES its own ordered places (ADR-0099) — the wardrobe tabs follow that order, not
    // a hardcoded category list, so a hero with different wear-places (Plan E's robot) just works.
    return catalog.places
      .map((place) => ({
        category: place.id,
        items: [...(byPlace.get(place.id) ?? [])].sort((a, b) => rank(a) - rank(b)),
      }))
      .filter((g) => g.items.length > 0);
  }, [catalog.items, catalog.places]);

  const [active, setActive] = useState<string>(
    () => grouped[0]?.category ?? catalog.places[0]?.id ?? '',
  );
  // Keep the selected tab valid when the set of places changes — e.g. the kid switched heroes (princess →
  // robot), so the catalog now exposes ENTIRELY different places and the old `active` no longer exists.
  useEffect(() => {
    if (grouped.length > 0 && !grouped.some((g) => g.category === active)) {
      setActive(grouped[0]!.category);
    }
  }, [grouped, active]);
  const [buyTarget, setBuyTarget] = useState<CosmeticCatalogItemResponse | null>(null);

  if (grouped.length === 0) {
    return <EmptyState glyph="✨" message={t('shop.empty')} description={t('shop.emptyHint')} />;
  }

  // Non-null: we returned above when grouped is empty, so grouped[0] is always present.
  const activeGroup = grouped.find((g) => g.category === active) ?? grouped[0]!;

  const tile = (item: CosmeticCatalogItemResponse): React.ReactElement => (
    <CosmeticTile
      key={item.itemId}
      item={item}
      stars={catalog.stars}
      onEquipToggle={onEquipToggle}
      onBuyIntent={preview ? preview.onTryOn : setBuyTarget}
      busy={buying.has(item.itemId) || equipping.has(item.itemId)}
    />
  );

  // THEME BROWSE (ADR-0113 Tier 1.5): a hero's looks within the active place split into per-theme shelves
  // (regal · magic · …) once there are ≥2 themes, so the skin shop browses by collection instead of one long
  // wall. A place with one theme (or none) stays a single flat grid — no needless headings for a short list.
  const themeGroups = groupByTheme(activeGroup.items, (i) => i.theme);
  const themed = themeGroups.filter((g) => g.theme !== null).length >= 2;

  return (
    <div className="wardrobe-shop flex flex-col gap-3">
      <WardrobeLegend />
      {/* The place tab bar only earns its keep when a hero has MORE THAN ONE wear-place. A skin hero with a
          single place (ADR-0110) would show a lone "Looks" tab that just repeats the page's "Skins" section
          tab — so suppress it and let the one place's grid fill the panel. */}
      {grouped.length > 1 && (
        <WardrobeTabBar
          categories={grouped.map((g) => g.category)}
          active={activeGroup.category}
          onSelect={setActive}
          idScope={idScope}
        />
      )}
      <div
        // Only a tabpanel when there's a tab bar above to label it (multi-place heroes); a single-place hero
        // renders a plain grid container so `aria-labelledby` never dangles to a suppressed tab.
        role={grouped.length > 1 ? 'tabpanel' : undefined}
        id={wardrobePanelId(idScope)}
        aria-labelledby={
          grouped.length > 1 ? wardrobeTabId(activeGroup.category, idScope) : undefined
        }
        tabIndex={grouped.length > 1 ? 0 : undefined}
      >
        {themed ? (
          <div className="flex flex-col gap-4">
            {themeGroups.map((g) => (
              <div key={g.theme ?? '_default'} className="wardrobe-theme">
                <h3 className="wardrobe-theme-h">
                  {g.theme ? tDynamic(tPlay, `look.theme.${g.theme}`) : t('shop.moreLooks')}
                </h3>
                <div className="tile-grid">{g.items.map(tile)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tile-grid">{activeGroup.items.map(tile)}</div>
        )}
      </div>

      {/* The kind purchase/save sheet only exists on the live kid surface — preview taps try-on instead. */}
      {!preview && (
        <BuyConfirmSheet
          item={buyTarget}
          stars={catalog.stars}
          busy={buyTarget ? buying.has(buyTarget.itemId) : false}
          onClose={() => setBuyTarget(null)}
          onConfirm={(item) => {
            livePurchase.buy(item.itemId, tDynamic(t, `common:cosmetics.item.${item.itemId}.name`));
            setBuyTarget(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The kind UNLOCK CONFIRM / SAVE sheet (ADR-0101 · ADR-0103, no dark patterns): tapping an unowned tile opens
 * this. Affordable → the item big, a full ✨ ring, "Spend N ✨", and an explicit Unlock (NEVER a one-tap
 * spend). Still saving → a partially-filled ✨ ring + "N ✨ to go" instead of an Unlock, framed as a goal,
 * never shaming. ✨ is shown as the concrete currency now (ADR-0103), not the old "≈ N days" estimate.
 */
function BuyConfirmSheet({
  item,
  stars,
  busy,
  onClose,
  onConfirm,
}: {
  item: CosmeticCatalogItemResponse | null;
  stars: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: (item: CosmeticCatalogItemResponse) => void;
}): React.ReactElement | null {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();
  const currencyFor = useScopedCurrencyNameFor();
  if (!item) return null;
  const itemName = tDynamic(t, `common:cosmetics.item.${item.itemId}.name`);
  const savePct =
    item.priceStars > 0 ? Math.min(100, Math.round((stars / item.priceStars) * 100)) : 100;
  const remaining = Math.max(0, item.priceStars - stars);

  return (
    <ActionSheet
      open
      onClose={onClose}
      title={itemName}
      footer={
        item.affordable ? (
          <button
            type="button"
            className="btn btn-gold btn-block"
            disabled={busy}
            onClick={() => onConfirm(item)}
          >
            {busy
              ? t('shop.buying')
              : `${t('shop.buy')} · ${t('shop.spend', { count: item.priceStars, currency: currencyFor(item.priceStars) })}`}
          </button>
        ) : undefined
      }
    >
      <div className="buy-confirm">
        <span className="buy-art" aria-hidden="true">
          <CosmeticPreview category={item.place} itemId={item.itemId} />
        </span>
        <StarRing
          pct={savePct}
          label={formatPoints(locale, item.priceStars)}
          ready={item.affordable}
        />
        {item.affordable ? (
          <p className="buy-spend">
            {t('shop.spend', { count: item.priceStars, currency: currencyFor(item.priceStars) })}
          </p>
        ) : (
          <div className="buy-save">
            {remaining > 0 && (
              <p className="buy-days">
                {t('shop.starsToGo', { count: remaining, currency: currencyFor(remaining) })}
              </p>
            )}
            <p className="buy-have">
              {t('shop.starsLabel', { count: stars })} / {formatPoints(locale, item.priceStars)}
            </p>
          </div>
        )}
      </div>
    </ActionSheet>
  );
}

/**
 * The spendable effort-star (✨) balance chip for the live-preview stage — a solid white `coin-chip` so the
 * amount reads clearly over the sky gradient. ✨ is the dress-up/hero currency (ADR-0101), never coins/XP.
 */
export function ShopStarBalance({ stars }: { stars: number }): React.ReactElement {
  const { locale } = useHouseholdContext();
  // The balance is hero-SKINNED (ADR-0103): the count + the active hero's currency name (e.g. "150 stars" /
  // "150 credits") + its glyph (via <CurrencyGlyph>). Reads the surrounding HeroCurrencyProvider.
  const currencyFor = useScopedCurrencyNameFor();
  return (
    <span
      className="coin-chip"
      role="img"
      aria-label={`${formatPoints(locale, stars)} ${currencyFor(stars)}`}
    >
      <span className="coin star" aria-hidden="true">
        <CurrencyGlyph />
      </span>
      <span aria-hidden="true">{formatPoints(locale, stars)}</span>
    </span>
  );
}
